/**
 * Applies the active brand's tokens as CSS custom properties on <html> and
 * manages light/dark (system-aware). Brand + theme are live-swappable — the
 * white-label promise, at runtime, with zero component changes.
 */
import { createContext, useContext, useEffect, useInsertionEffect, useMemo, useState, type ReactNode } from 'react'
import { cssVars, type BrandTokens, type Density } from '@solum/experience'
import { BRANDS, type HeaderTreatment, type ThemeMode } from './tokens'

/** A shallow-per-section partial of the brand tokens — what a settings UI edits. */
export interface BrandGradientEndpoints { from: string; to: string }
export type BrandPatch = Partial<Omit<BrandTokens, 'colors' | 'font' | 'gradient'>> & {
  colors?: Partial<BrandTokens['colors']>
  font?: Partial<BrandTokens['font']>
  /** Persisted deployment documents use endpoints; the browser uses CSS. */
  gradient?: string | BrandGradientEndpoints
}

interface ThemeCtx {
  brand: BrandTokens              // the ACTIVE brand = base brand + any live overrides
  brands: BrandTokens[]
  brandIndex: number
  setBrandIndex: (i: number) => void
  mode: ThemeMode
  setMode: (m: ThemeMode) => void
  /** the user's control rhythm — compact / default / comfortable. */
  density: Density
  setDensity: (d: Density) => void
  /**
   * How a PROMINENT header is coloured — `match` or `contrast` (#2347 §1).
   *
   * A third personal browser preference with the same custody as `mode` and
   * `density`, and INDEPENDENT of `mode`: the page stays light while its
   * product headers stay dark. Only components with a declared prominent
   * header slot consume it; nothing else in the tree branches on this value.
   */
  headerTreatment: HeaderTreatment
  setHeaderTreatment: (h: HeaderTreatment) => void
  resolved: 'light' | 'dark'
  toggle: () => void
  /** Live-edit the active brand's tokens (the branding settings surface). Merges
   *  the patch, re-applies the CSS vars app-wide, and persists per-brand. */
  updateBrand: (patch: BrandPatch) => void
  /** Revert the active brand to its shipped token defaults. */
  resetBrand: () => void
  /** true when the active brand carries user overrides. */
  isCustomised: boolean
  /** the active brand's current override patch (for persisting to the deployment). */
  override?: BrandPatch
  /** Apply an override without re-persisting to localStorage — used to hydrate the
   *  active brand FROM an external store (the deployment's saved brand). */
  hydrateBrand: (patch: BrandPatch) => void
}
const Ctx = createContext<ThemeCtx | null>(null)

export interface ThemeProviderProps {
  children: ReactNode
  /** Host-specific opening mode. Existing hosts keep the active brand default. */
  initialMode?: ThemeMode
  /**
   * Isolated hosts may lock their chrome to one mode. A lock is a host
   * boundary, not a user preference: setMode/toggle become deliberate no-ops
   * and no stored/system preference can change it.
   */
  lockedMode?: Exclude<ThemeMode, 'system'>
}

const HEX = /^#[0-9a-f]{6}$/i
const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/** Turn persisted JSON into the runtime token shape before it reaches CSS. */
export function normalizeBrandPatch(base: BrandTokens, value: unknown): BrandPatch {
  if (!isRecord(value)) return {}
  const { gradient: rawGradient, colors: rawColors, font: rawFont, ...rest } = value
  let gradient: string | undefined
  if (typeof rawGradient === 'string') gradient = rawGradient
  else if (isRecord(rawGradient)
    && typeof rawGradient.from === 'string' && HEX.test(rawGradient.from)
    && typeof rawGradient.to === 'string' && HEX.test(rawGradient.to)) {
    /* The middle stop is the EFFECTIVE brand colour: the patch's own when it
       supplies one, the base brand's only when it does not. Reading the base
       unconditionally put Moonbase seafoam inside every other deployment's
       sweep (MB-Opera #91 G3). */
    const middle = isRecord(rawColors) && typeof rawColors.brand === 'string' && HEX.test(rawColors.brand)
      ? rawColors.brand
      : base.colors.brand
    gradient = `linear-gradient(135deg, ${rawGradient.from}, ${middle} 55%, ${rawGradient.to})`
  }
  return {
    ...rest,
    ...(isRecord(rawColors) ? { colors: rawColors as Partial<BrandTokens['colors']> } : {}),
    ...(isRecord(rawFont) ? { font: rawFont as Partial<BrandTokens['font']> } : {}),
    ...(gradient ? { gradient } : {}),
  } as BrandPatch
}

const mergeBrand = (base: BrandTokens, value?: BrandPatch): BrandTokens => {
  if (!value) return base
  const p = normalizeBrandPatch(base, value)
  return {
    ...base,
    ...p,
    gradient: typeof p.gradient === 'string' ? p.gradient : base.gradient,
    colors: { ...base.colors, ...(p.colors ?? {}) },
    font: { ...base.font, ...(p.font ?? {}) },
  }
}

const OVR_KEY = 'solum.brand.overrides'
const MODE_KEY = 'solum.theme'
const DENSITY_KEY = 'solum.density'
const HEADER_KEY = 'solum.header-treatment'
const MODES: ThemeMode[] = ['light', 'dark', 'system']
const DENSITIES: Density[] = ['compact', 'default', 'comfortable']
const HEADER_TREATMENTS: HeaderTreatment[] = ['match', 'contrast']
const loadMode = (): ThemeMode | undefined => {
  try {
    const v = localStorage.getItem(MODE_KEY)
    return MODES.includes(v as ThemeMode) ? (v as ThemeMode) : undefined
  } catch { return undefined }
}
const loadDensity = (): Density | undefined => {
  try {
    const v = localStorage.getItem(DENSITY_KEY)
    return DENSITIES.includes(v as Density) ? (v as Density) : undefined
  } catch { return undefined }
}
/**
 * The stored header treatment, or nothing.
 *
 * `includes` and not a cast: §1 asks for a finite value and for malformed
 * stored values to *"fall back to the declared default"*. Returning `undefined`
 * for anything outside the list is what makes that true for a typo, a stale
 * value from a future version, and `null` alike — the caller then applies the
 * brand's declared default, so there is one answer rather than three.
 */
const loadHeaderTreatment = (): HeaderTreatment | undefined => {
  try {
    const v = localStorage.getItem(HEADER_KEY)
    return HEADER_TREATMENTS.includes(v as HeaderTreatment) ? (v as HeaderTreatment) : undefined
  } catch { return undefined }
}
const loadOverrides = (): Record<string, BrandPatch> => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(OVR_KEY) || '{}')
    if (!isRecord(stored)) return {}
    const normalised: Record<string, BrandPatch> = {}
    for (const base of BRANDS) {
      if (Object.prototype.hasOwnProperty.call(stored, base.name)) {
        normalised[base.name] = normalizeBrandPatch(base, stored[base.name])
      }
    }
    return normalised
  } catch { return {} }
}

export function useTheme(): ThemeCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useTheme must be used within <ThemeProvider>')
  return c
}

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches
const resolve = (m: ThemeMode): 'light' | 'dark' => (m === 'system' ? (prefersDark() ? 'dark' : 'light') : m)

/**
 * Apply the brand at runtime. The property LIST comes from `cssVars` — the same
 * function the static renderer's `tokensToCss` uses — so the two paths cannot
 * disagree about what a brand emits. They were two hand-maintained copies of one
 * list, which is precisely how `--shadow-lg` ended up used in two modules and
 * defined in neither.
 */
function apply(
  brand: BrandTokens, resolved: 'light' | 'dark', density: Density,
  headerTreatment: HeaderTreatment,
) {
  const r = document.documentElement.style
  for (const [k, v] of Object.entries(cssVars(brand, resolved))) r.setProperty(k, v)
  document.documentElement.setAttribute('data-theme', resolved)
  // Density is the USER's, not the brand's — the brand only supplies the default.
  document.documentElement.setAttribute('data-density', density)
  /* THE SAME PASS as `data-theme`, which is the whole of #2347 §5's first-paint
     clause: *"apply theme and header treatment in the same insertion-effect
     pass so a light page never flashes a light feature header before becoming
     contrast."* A second effect, a second attribute writer or a host-side
     `useEffect` would each reintroduce exactly that frame — there is one
     writer, and it is this line. */
  document.documentElement.setAttribute('data-header-treatment', headerTreatment)
}

export function ThemeProvider({ children, initialMode, lockedMode }: ThemeProviderProps) {
  const [brandIndex, setBrandIndex] = useState(0)
  // First load follows the active brand's declared preference (Moonbase = light-first
  // since 2026-08-03); the toggle overrides at runtime. Brands without a preference
  // fall back to 'system'.
  const [preferredMode, setPreferredMode] = useState<ThemeMode>(
    () => initialMode ?? loadMode() ?? BRANDS[0]?.defaultMode ?? 'system',
  )
  const mode: ThemeMode = lockedMode ?? preferredMode
  // Live, per-brand token overrides — the branding settings surface writes here.
  const [overrides, setOverrides] = useState<Record<string, BrandPatch>>(loadOverrides)
  // Control rhythm. The brand supplies a default; the USER's choice wins and is
  // remembered, because density is a preference and not an identity — a power
  // user with 200 grid rows wants compact, and taking that away would be the
  // wrong read of "the controls are too dense". What changed is the DEFAULT.
  const [density, setDensityState] = useState<Density>(() => loadDensity() ?? BRANDS[0]?.density ?? 'default')
  /* Header treatment. DEFAULT `contrast` — §1: *"Default Moonbase experience:
     `contrast`. This is the approved house style."*
     A platform constant rather than a per-brand field, deliberately: §1 says a
     white-label brand *"reuses their own already-proven inverse pair"*, which
     is about the VALUE and is already true through the derived tokens. It does
     not ask each brand to choose the preference, and adding a `BrandTokens`
     field nobody varies would be a judgement stored in four places (rule 42). */
  const [headerTreatment, setHeaderTreatmentState] = useState<HeaderTreatment>(
    () => loadHeaderTreatment() ?? 'contrast',
  )

  const base = BRANDS[brandIndex]!
  const brand = useMemo(() => mergeBrand(base, overrides[base.name]), [base, overrides])
  const resolved = resolve(mode)

  // Insertion effect is intentional: tokens and data-theme are on <html>
  // before any layout effect and before the browser paints the host. A normal
  // effect produces one light frame in a locked-dark extension panel, which is
  // not a cosmetic defect in a surface that opens over another application.
  useInsertionEffect(() => { apply(brand, resolved, density, headerTreatment) },
    [brand, resolved, density, headerTreatment])
  useEffect(() => {
    if (mode !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => apply(brand, mq.matches ? 'dark' : 'light', density, headerTreatment)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [mode, brand, density, headerTreatment])

  const persist = (o: Record<string, BrandPatch>) => { try { localStorage.setItem(OVR_KEY, JSON.stringify(o)) } catch { /* */ } }

  const value = useMemo<ThemeCtx>(() => ({
    brand, brands: BRANDS, brandIndex, setBrandIndex, mode,
    setMode: (next: ThemeMode) => {
      if (lockedMode) return
      setPreferredMode(next)
      try { localStorage.setItem(MODE_KEY, next) } catch { /* private mode */ }
    },
    resolved,
    density,
    setDensity: (d: Density) => {
      setDensityState(d)
      try { localStorage.setItem(DENSITY_KEY, d) } catch { /* private mode */ }
    },
    headerTreatment,
    setHeaderTreatment: (h: HeaderTreatment) => {
      setHeaderTreatmentState(h)
      try { localStorage.setItem(HEADER_KEY, h) } catch { /* private mode */ }
    },
    toggle: () => {
      if (lockedMode) return
      const next = resolved === 'dark' ? 'light' : 'dark'
      setPreferredMode(next)
      try { localStorage.setItem(MODE_KEY, next) } catch { /* private mode */ }
    },
    isCustomised: !!overrides[base.name],
    override: overrides[base.name],
    updateBrand: (patch) => setOverrides((o) => {
      const cur = o[base.name] ?? {}
      const merged = { ...cur, ...patch,
        colors: { ...(cur.colors ?? {}), ...(patch.colors ?? {}) },
        font: { ...(cur.font ?? {}), ...(patch.font ?? {}) } }
      const next = { ...o, [base.name]: normalizeBrandPatch(base, merged) }
      persist(next); return next
    }),
    // Replace (not merge) the active brand's override from the deployment's saved
    // brand, and cache it locally so it survives the next offline load too.
    hydrateBrand: (patch) => setOverrides((o) => {
      const next = { ...o, [base.name]: normalizeBrandPatch(base, patch) }
      persist(next); return next
    }),
    resetBrand: () => setOverrides((o) => { const next = { ...o }; delete next[base.name]; persist(next); return next }),
  }), [brand, base, brandIndex, mode, resolved, overrides, density, headerTreatment, lockedMode])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
